import { ApiRequestError, api, apiFormData } from '@/lib/api';
import { clockOffsetMs } from '@/lib/attempt-clock';
import { attemptConnection } from '@/lib/attempt-connection';
import { browserAttemptStore, connectionPeriods, withProgressAck } from '@/lib/attempt-offline-store';
import { clearAttemptFiles, deleteAttemptFile, listAttemptFiles, readAttemptFile } from '@/lib/attempt-file-queue';
import { createAttemptSyncQueue, jobsForDraft, type SyncJob } from '@/lib/attempt-sync-queue';

async function statusOf(run: () => Promise<unknown>): Promise<{ status: number; body?: unknown }> {
  const started = attemptConnection.begin();
  try {
    const body = await run();
    attemptConnection.report(200, started);
    return { status: 200, body };
  } catch (error) {
    const status = error instanceof ApiRequestError ? error.status : 0;
    attemptConnection.report(status, started);
    return { status };
  }
}

function responseRows(answers: Record<string, unknown>) {
  return Object.entries(answers).map(([questionId, response]) => ({ questionId, response }));
}

export async function deliverAttemptJob(job: SyncJob): Promise<{ status: number; body?: unknown }> {
  const draft = browserAttemptStore().load(job.userId, job.attemptId);
  if (job.kind === 'progress') {
    if (!draft) return { status: 200 };
    return statusOf(() =>
      api(draft.progressUrl, {
        method: 'POST',
        body: {
          responses: responseRows(job.answers ?? draft.answers),
          deviceId: job.deviceId ?? draft.deviceId,
          seq: job.seq ?? draft.seq,
        },
      }),
    );
  }
  if (job.kind === 'file') {
    if (!draft || !job.idempotencyKey || !job.questionId) return { status: 200 };
    const file = await readAttemptFile(job.idempotencyKey);
    if (!file) return { status: 200 };
    const status = await statusOf(() => {
      const form = new FormData();
      form.append('file', file.blob, file.name);
      return apiFormData(`${draft.fileUrl}/${job.questionId}/file`, form, {
        headers: { 'Idempotency-Key': job.idempotencyKey ?? '' },
      });
    });
    if (status.status >= 200 && status.status < 300) await deleteAttemptFile(job.idempotencyKey);
    return status;
  }
  if (!draft || !draft.pendingSubmit) return { status: 200 };
  const periods = connectionPeriods(draft.offlineLog);
  return statusOf(() =>
    api(draft.submitUrl, {
      method: 'POST',
      body: {
        responses: responseRows(job.answers ?? draft.answers),
        clientSubmissionId: job.clientSubmissionId ?? draft.clientSubmissionId,
        ...(draft.queuedAt ? { queuedAt: draft.queuedAt } : {}),
        ...(periods.length > 0 ? { connectionGaps: periods } : {}),
      },
    }),
  );
}

export async function syncStoredAttempts(userId: string): Promise<void> {
  const store = browserAttemptStore();
  const queue = createAttemptSyncQueue(async (job) => {
    const result = await deliverAttemptJob(job);
    if (result.status >= 200 && result.status < 300 && job.kind === 'progress' && result.body && typeof result.body === 'object') {
      const draft = store.load(job.userId, job.attemptId);
      if (draft) {
        const body = result.body as { acceptedSeq?: number | null; deadlineAt?: string | null; graceUntil?: string | null; serverNow?: string | null };
        store.save(job.userId, job.attemptId, withProgressAck(draft, body, new Date().toISOString(), body.serverNow ? clockOffsetMs(body.serverNow) : null));
      }
    }
    if ((result.status >= 200 && result.status < 300 && job.kind === 'submit') || result.status === 409) {
      store.clear(job.userId, job.attemptId);
      await clearAttemptFiles(job.userId, job.attemptId);
    }
    return result.status;
  });
  for (const row of store.listUser(userId)) {
    for (const job of jobsForDraft(userId, row.attemptId, row.draft)) queue.enqueue(job);
    const files = await listAttemptFiles(userId, row.attemptId);
    for (const file of files) {
      queue.enqueue({
        kind: 'file',
        userId,
        attemptId: row.attemptId,
        questionId: file.questionId,
        idempotencyKey: file.idempotencyKey,
      });
    }
  }
  await queue.drain();
}
